import React, { useState, useEffect } from 'react';
import { Box, Container, Paper, Typography, Button, Divider, Grid, Avatar, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, IconButton, Alert, Checkbox, FormControlLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { setUser } from '../store/slices/authSlice';
import axiosInstance from '../api/axiosConfig';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSnackbar } from '../components/common/SnackbarProvider';

const ProfilePage: React.FC = () => {
  const isMobile = useIsMobile();
  const snackbar = useSnackbar();
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [dependents, setDependents] = useState<any[]>([]);
  const [editDependentDialog, setEditDependentDialog] = useState(false);
  const [selectedDependent, setSelectedDependent] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Change-password dialog state
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  function openPwDialog() {
    setPwCurrent('');
    setPwNew('');
    setPwConfirm('');
    setPwError(null);
    setPwOpen(true);
  }

  async function submitPasswordChange() {
    setPwError(null);
    const minLen = isStaffUser() ? 8 : 6;
    if (!pwCurrent || !pwNew || !pwConfirm) {
      setPwError('Please fill in all three fields');
      return;
    }
    if (pwNew.length < minLen) {
      setPwError(`New password must be at least ${minLen} characters`);
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('New password and confirmation do not match');
      return;
    }
    if (pwNew === pwCurrent) {
      setPwError('New password must differ from your current password');
      return;
    }
    setPwBusy(true);
    try {
      const endpoint = isStaffUser()
        ? '/staff/me/change-password'
        : '/members/me/change-password';
      await axiosInstance.put(endpoint, {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      setPwOpen(false);
      snackbar.success('Password updated');
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string; message?: string } }; message?: string };
      const err = e as ErrLike;
      setPwError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to change password',
      );
    } finally {
      setPwBusy(false);
    }
  }

  useEffect(() => {
    fetchUserProfile();
    if (isStaffUser()) {
      // Staff don't have dependents
    } else {
      fetchDependents();
    }
  }, []);

  const isStaffUser = () => {
    return user?.role && ['STAFF', 'ADMIN', 'SUPERUSER'].includes(user.role.toUpperCase());
  };

  // Non-members are Member rows whose membership_status starts with "non_member_".
  // Their dependents live in the NonMemberFamily table, not /members/me/linked.
  const isNonMemberUser = () =>
    (user?.membership_status || '').toLowerCase().startsWith('non_member');

  const fetchUserProfile = async () => {
    try {
      // Use different endpoint based on user role
      const endpoint = isStaffUser() ? '/staff/me' : '/members/me';
      const response = await axiosInstance.get(endpoint);
      if (response.data.data) {
        dispatch(setUser(response.data.data));
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const fetchDependents = async () => {
    try {
      // Non-members keep their family in the NonMemberFamily table, reached via
      // /non-members/me/family. The member-only /members/me/linked is empty for them.
      if (isNonMemberUser()) {
        const familyResponse = await axiosInstance.get('/non-members/me/family');
        const familyRows = familyResponse.data.data || [];
        const mapped = familyRows.map((row: any) => ({
          id: row.id,
          first_name: row.first_name,
          last_name: row.last_name,
          date_of_birth: row.date_of_birth,
          relationship_to_primary: row.relationship_type,
        }));
        setDependents(mapped);
        return;
      }
      const linkedMembersResponse = await axiosInstance.get('/members/me/linked', {
        params: { include_dependents: true }
      });
      const allLinkedMembers = linkedMembersResponse.data.data || [];
      const dependentMembers = allLinkedMembers.filter((m: any) => m.member_type === 'DEPENDENT');
      setDependents(dependentMembers);
    } catch (error) {
      console.error('Failed to fetch dependents:', error);
    }
  };

  const handleOpenEditDependent = (dependent: any) => {
    setSelectedDependent({
      id: dependent.id,
      first_name: dependent.first_name || '',
      last_name: dependent.last_name || '',
      date_of_birth: dependent.date_of_birth || '',
      emergency_contact_name: dependent.emergency_contact_name || '',
      emergency_contact_phone: dependent.emergency_contact_phone || '',
      emergency_contact_email: dependent.emergency_contact_email || '',
      emergency_contact_relationship: dependent.emergency_contact_relationship || '',
      medical_conditions: dependent.medical_conditions || '',
      allergies: dependent.allergies || '',
      dietary_restrictions: dependent.dietary_restrictions || '',
      activity_interests: dependent.activity_interests || {
        cycling: false,
        group_classes: false,
        gym: false,
        pickleball: false,
        socializing: false,
        swim: false,
        tennis: false,
        yoga: false,
      },
    });
    setEditDependentDialog(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseEditDependent = () => {
    setEditDependentDialog(false);
    setSelectedDependent(null);
    setError(null);
    setSuccess(null);
  };

  const handleSaveDependent = async () => {
    if (!selectedDependent) return;

    setError(null);
    setSuccess(null);

    try {
      await axiosInstance.put(`/members/${selectedDependent.id}`, {
        emergency_contact_name: selectedDependent.emergency_contact_name,
        emergency_contact_phone: selectedDependent.emergency_contact_phone,
        emergency_contact_email: selectedDependent.emergency_contact_email,
        emergency_contact_relationship: selectedDependent.emergency_contact_relationship,
        medical_conditions: selectedDependent.medical_conditions,
        allergies: selectedDependent.allergies,
        dietary_restrictions: selectedDependent.dietary_restrictions,
        activity_interests: selectedDependent.activity_interests,
      });

      setSuccess('Dependent information updated successfully');
      fetchDependents();

      // Close dialog after 1.5 seconds
      setTimeout(() => {
        handleCloseEditDependent();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update dependent information');
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 0 }, mb: 3 }}>
            <Typography variant="h4" component="h1">
              My Profile
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="outlined"
                startIcon={<LockResetIcon />}
                onClick={openPwDialog}
                fullWidth={isMobile}
              >
                Change Password
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate('/profile/edit')}
                fullWidth={isMobile}
              >
                Edit Profile
              </Button>
            </Stack>
          </Box>

          {user && (
            <Box>
              {/* Profile Photo */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <Avatar
                  src={user.profile_photo_url || undefined}
                  sx={{ width: 120, height: 120 }}
                >
                  {!user.profile_photo_url && `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`}
                </Avatar>
              </Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Personal Information
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{user.first_name} {user.last_name}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{user.email}</Typography>
                </Grid>
                {user.date_of_birth && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                    <Typography variant="body1">
                      {(() => {
                        const [year, month, day] = user.date_of_birth.split('-');
                        return `${month}/${day}/${year}`;
                      })()}
                    </Typography>
                  </Grid>
                )}
                {user.gender && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Gender</Typography>
                    <Typography variant="body1">
                      {user.gender === 'MALE' ? 'Male' :
                       user.gender === 'FEMALE' ? 'Female' :
                       user.gender === 'OTHER' ? 'Other' :
                       'Prefer not to say'}
                    </Typography>
                  </Grid>
                )}
                {/* Occupation + Employer view rows removed to match the trimmed Edit Profile (2026-06-01). */}
                {user.spouse_name && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Spouse</Typography>
                    <Typography variant="body1">{user.spouse_name}</Typography>
                  </Grid>
                )}
                {user.anniversary_date && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Anniversary Date</Typography>
                    <Typography variant="body1">
                      {(() => {
                        const [year, month, day] = user.anniversary_date.split('-');
                        return `${month}/${day}/${year}`;
                      })()}
                    </Typography>
                  </Grid>
                )}
                {dependents.length > 0 && dependents.map((dependent: any, index: number) => (
                  <Grid size={{ xs: 12 }} key={dependent.id || index}>
                    <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Dependent {index + 1}</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {dependent.first_name} {dependent.last_name}
                        </Typography>
                        {dependent.date_of_birth && (
                          <Typography variant="body2" color="text.secondary">
                            DOB: {(() => {
                              const [year, month, day] = dependent.date_of_birth.split('-');
                              return `${month}/${day}/${year}`;
                            })()}
                          </Typography>
                        )}
                      </Box>
                      {/* The Manage flow edits a Member row (PUT /members/:id).
                          Non-member family lives in a separate table and isn't
                          editable here, so the button is member-only. */}
                      {!isNonMemberUser() && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenEditDependent(dependent)}
                        >
                          Manage
                        </Button>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                {user.mobile_phone && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Mobile Phone</Typography>
                    <Typography variant="body1">{user.mobile_phone}</Typography>
                  </Grid>
                )}
                {user.home_phone && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Home Phone</Typography>
                    <Typography variant="body1">{user.home_phone}</Typography>
                  </Grid>
                )}
                {user.work_phone && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Work Phone</Typography>
                    <Typography variant="body1">{user.work_phone}</Typography>
                  </Grid>
                )}
                {user.secondary_email && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Secondary Email</Typography>
                    <Typography variant="body1">{user.secondary_email}</Typography>
                  </Grid>
                )}
                {user.preferred_contact_method && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Preferred Contact Method</Typography>
                    <Typography variant="body1">
                      {user.preferred_contact_method === 'EMAIL' ? 'Email' :
                       user.preferred_contact_method === 'PHONE' ? 'Phone' :
                       user.preferred_contact_method === 'SMS' ? 'SMS' :
                       user.preferred_contact_method === 'MAIL' ? 'Mail' : ''}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {(user.address_line1 || user.city) && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Home Address
                  </Typography>
                  {user.address_line1 && <Typography variant="body1">{user.address_line1}</Typography>}
                  {user.address_line2 && <Typography variant="body1">{user.address_line2}</Typography>}
                  {(user.city || user.state || user.postal_code) && (
                    <Typography variant="body1">
                      {user.city}{user.city && user.state ? ', ' : ''}{user.state} {user.postal_code}
                    </Typography>
                  )}
                  {user.country && <Typography variant="body1">{user.country}</Typography>}
                </>
              )}

              {!user.mailing_same_as_home && (user.mailing_address_line1 || user.mailing_city) && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Mailing Address
                  </Typography>
                  {user.mailing_address_line1 && <Typography variant="body1">{user.mailing_address_line1}</Typography>}
                  {user.mailing_address_line2 && <Typography variant="body1">{user.mailing_address_line2}</Typography>}
                  {(user.mailing_city || user.mailing_state || user.mailing_postal_code) && (
                    <Typography variant="body1">
                      {user.mailing_city}{user.mailing_city && user.mailing_state ? ', ' : ''}{user.mailing_state} {user.mailing_postal_code}
                    </Typography>
                  )}
                  {user.mailing_country && <Typography variant="body1">{user.mailing_country}</Typography>}
                </>
              )}

              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                {user.role === 'staff' || user.role === 'admin' || user.role === 'superuser'
                  ? 'Employee Information'
                  : 'Membership Information'}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Typography variant="body1">{user.membership_status}</Typography>
                </Grid>
                {user.member_number && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {user.role === 'staff' || user.role === 'admin' || user.role === 'superuser'
                        ? 'Employee Number'
                        : 'Member Number'}
                    </Typography>
                    <Typography variant="body1">{user.member_number}</Typography>
                  </Grid>
                )}
                {user.joined_date && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {user.role === 'staff' || user.role === 'admin' || user.role === 'superuser'
                        ? 'Hired Date'
                        : 'Member Since'}
                    </Typography>
                    <Typography variant="body1">
                      {(() => {
                        const [year, month, day] = user.joined_date.split('-');
                        return `${month}/${day}/${year}`;
                      })()}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {user.activity_interests && Object.values(user.activity_interests).some((val: any) => val === true) && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Activity Interests
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {user.activity_interests.cycling && <Chip label="Cycling" color="primary" />}
                    {user.activity_interests.group_classes && <Chip label="Group Classes" color="primary" />}
                    {user.activity_interests.gym && <Chip label="Gym" color="primary" />}
                    {user.activity_interests.pickleball && <Chip label="Pickleball" color="primary" />}
                    {user.activity_interests.socializing && <Chip label="Socializing" color="primary" />}
                    {user.activity_interests.swim && <Chip label="Swimming" color="primary" />}
                    {user.activity_interests.tennis && <Chip label="Tennis" color="primary" />}
                    {user.activity_interests.yoga && <Chip label="Yoga" color="primary" />}
                  </Box>
                </>
              )}

              {user.emergency_contact_name && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Emergency Contact
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">Name</Typography>
                      <Typography variant="body1">{user.emergency_contact_name}</Typography>
                    </Grid>
                    {user.emergency_contact_phone && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">Phone</Typography>
                        <Typography variant="body1">{user.emergency_contact_phone}</Typography>
                      </Grid>
                    )}
                    {/* Emergency Contact Email view row removed to match the trimmed Edit Profile (2026-06-01). */}
                    {user.emergency_contact_relationship && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">Relationship</Typography>
                        <Typography variant="body1">{user.emergency_contact_relationship}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {(user.medical_conditions || user.allergies || user.dietary_restrictions) && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Health & Dietary Information
                  </Typography>
                  <Grid container spacing={2}>
                    {user.medical_conditions && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="body2" color="text.secondary">Medical Conditions</Typography>
                        <Typography variant="body1">{user.medical_conditions}</Typography>
                      </Grid>
                    )}
                    {user.allergies && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="body2" color="text.secondary">Allergies</Typography>
                        <Typography variant="body1">{user.allergies}</Typography>
                      </Grid>
                    )}
                    {user.dietary_restrictions && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="body2" color="text.secondary">Dietary Restrictions</Typography>
                        <Typography variant="body1">{user.dietary_restrictions}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {/* Privacy & Communication Preferences view section removed to match the trimmed Edit Profile (2026-06-01). */}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Edit Dependent Dialog */}
      <Dialog open={editDependentDialog} onClose={handleCloseEditDependent} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Manage {selectedDependent?.first_name} {selectedDependent?.last_name}
            </Typography>
            <IconButton onClick={handleCloseEditDependent} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {selectedDependent && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Basic Information (Read-only)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={selectedDependent.first_name}
                  InputProps={{ readOnly: true }}
                  disabled
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={selectedDependent.last_name}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Box>
              <TextField
                fullWidth
                label="Date of Birth"
                value={selectedDependent.date_of_birth ? (() => {
                  const [year, month, day] = selectedDependent.date_of_birth.split('-');
                  return `${month}/${day}/${year}`;
                })() : 'Not set'}
                InputProps={{ readOnly: true }}
                disabled
              />

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                Emergency Contact
              </Typography>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                value={selectedDependent.emergency_contact_name}
                onChange={(e) => setSelectedDependent({ ...selectedDependent, emergency_contact_name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                value={selectedDependent.emergency_contact_phone}
                onChange={(e) => setSelectedDependent({ ...selectedDependent, emergency_contact_phone: e.target.value })}
              />
              {/* Emergency Contact Email input removed to match the trimmed profile (2026-06-01);
                  selectedDependent.emergency_contact_email is still loaded/saved so data is preserved. */}
              <TextField
                fullWidth
                label="Emergency Contact Relationship"
                value={selectedDependent.emergency_contact_relationship}
                onChange={(e) => setSelectedDependent({ ...selectedDependent, emergency_contact_relationship: e.target.value })}
              />

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                Health & Dietary Information
              </Typography>
              <TextField
                fullWidth
                label="Medical Conditions"
                multiline
                rows={2}
                value={selectedDependent.medical_conditions}
                onChange={(e) => setSelectedDependent({ ...selectedDependent, medical_conditions: e.target.value })}
                helperText="Any medical conditions staff should be aware of"
              />
              <TextField
                fullWidth
                label="Allergies"
                multiline
                rows={2}
                value={selectedDependent.allergies}
                onChange={(e) => setSelectedDependent({ ...selectedDependent, allergies: e.target.value })}
                helperText="Any allergies to food, medication, etc."
              />
              <TextField
                fullWidth
                label="Dietary Restrictions"
                multiline
                rows={2}
                value={selectedDependent.dietary_restrictions}
                onChange={(e) => setSelectedDependent({ ...selectedDependent, dietary_restrictions: e.target.value })}
                helperText="Any dietary restrictions or preferences"
              />

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                Activity Interests
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select activities your dependent is interested in
              </Typography>

              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedDependent.activity_interests?.cycling || false}
                        onChange={(e) => setSelectedDependent({
                          ...selectedDependent,
                          activity_interests: {
                            ...selectedDependent.activity_interests,
                            cycling: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Cycling"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedDependent.activity_interests?.group_classes || false}
                        onChange={(e) => setSelectedDependent({
                          ...selectedDependent,
                          activity_interests: {
                            ...selectedDependent.activity_interests,
                            group_classes: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Group Classes"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedDependent.activity_interests?.gym || false}
                        onChange={(e) => setSelectedDependent({
                          ...selectedDependent,
                          activity_interests: {
                            ...selectedDependent.activity_interests,
                            gym: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Gym"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedDependent.activity_interests?.pickleball || false}
                        onChange={(e) => setSelectedDependent({
                          ...selectedDependent,
                          activity_interests: {
                            ...selectedDependent.activity_interests,
                            pickleball: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Pickleball"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedDependent.activity_interests?.socializing || false}
                        onChange={(e) => setSelectedDependent({
                          ...selectedDependent,
                          activity_interests: {
                            ...selectedDependent.activity_interests,
                            socializing: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Socializing"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedDependent.activity_interests?.swim || false}
                        onChange={(e) => setSelectedDependent({
                          ...selectedDependent,
                          activity_interests: {
                            ...selectedDependent.activity_interests,
                            swim: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Swimming"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedDependent.activity_interests?.tennis || false}
                        onChange={(e) => setSelectedDependent({
                          ...selectedDependent,
                          activity_interests: {
                            ...selectedDependent.activity_interests,
                            tennis: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Tennis"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedDependent.activity_interests?.yoga || false}
                        onChange={(e) => setSelectedDependent({
                          ...selectedDependent,
                          activity_interests: {
                            ...selectedDependent.activity_interests,
                            yoga: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Yoga"
                  />
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDependent}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDependent}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password dialog — self-service, verifies current password
          before saving. Different from the admin reset path. */}
      <Dialog
        open={pwOpen}
        onClose={() => !pwBusy && setPwOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Enter your current password, then choose a new one
              {isStaffUser() ? ' (at least 8 characters).' : ' (at least 6 characters).'}
            </Typography>
            {pwError && <Alert severity="error" onClose={() => setPwError(null)}>{pwError}</Alert>}
            <TextField
              label="Current password"
              type="password"
              autoComplete="current-password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="New password"
              type="password"
              autoComplete="new-password"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              fullWidth
            />
            <TextField
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            '& > button': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
          }}
        >
          <Button onClick={() => setPwOpen(false)} disabled={pwBusy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void submitPasswordChange()}
            disabled={pwBusy}
          >
            {pwBusy ? 'Saving…' : 'Update password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfilePage;
